import { useState, useEffect } from "react";
import { CourseStageUIType } from "../types/promotionResponse";

// Storage key for filter state
const FILTER_STATE_KEY = "student_promotion_filter_state";

export type PromotionFilterState = {
  selectedSession: string;
  selectedCourse: string;
  selectedFromStage: string;
  selectedToStage: string;
  pageNumber: number;
  recordsPerPage: number;
  search: string;
};

export const usePromotionFilters = () => {
  // Get stored filter state from session storage
  const getStoredFilterState = (): PromotionFilterState | null => {
    const storedState = sessionStorage.getItem(FILTER_STATE_KEY);
    if (storedState) {
      try {
        return JSON.parse(storedState);
      } catch (e) {
        console.error("Error parsing stored filter state:", e);
      }
    }
    return null;
  };

  const storedState = getStoredFilterState();

  // State for filters
  const [selectedSession, setSelectedSession] = useState<string>(
    storedState?.selectedSession || ""
  );

  const [selectedCourse, setSelectedCourse] = useState<string>(
    storedState?.selectedCourse || ""
  );

  const [selectedFromStage, setSelectedFromStage] = useState<string>(
    storedState?.selectedFromStage || ""
  );

  const [selectedToStage, setSelectedToStage] = useState<string>(
    storedState?.selectedToStage || ""
  );

  const [recordsPerPage, setRecordsPerPage] = useState<number>(
    storedState?.recordsPerPage || 10
  );

  const [pageNumber, setPageNumber] = useState<number>(
    storedState?.pageNumber || 1
  );

  const [search, setSearch] = useState<string>(storedState?.search || "");

  // Course stages state
  const [courseStages, setCourseStages] = useState<CourseStageUIType[]>([]);

  // Store filter state in session storage whenever it changes
  useEffect(() => {
    const filterState = {
      selectedSession,
      selectedCourse,
      selectedFromStage,
      selectedToStage,
      recordsPerPage,
      pageNumber,
      search,
    };
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filterState));
  }, [
    selectedSession,
    selectedCourse,
    selectedFromStage,
    selectedToStage,
    recordsPerPage,
    pageNumber,
    search,
  ]);

  // Handler functions for filter changes
  const handleSessionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSession(e.target.value);
    setSelectedCourse("");
    setSelectedFromStage("");
    setSelectedToStage("");
    setPageNumber(1); // Reset to first page on filter change
  };

  const handleCourseChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    courses: any[]
  ) => {
    const courseId = e.target.value;
    setSelectedCourse(courseId);

    // Update course stages
    if (courseId) {
      const selectedCourseObj = courses.find(
        (course) => course.id.toString() === courseId
      );
      if (selectedCourseObj?.courseStages) {
        setCourseStages(
          selectedCourseObj.courseStages.map((stage: any, index: number) => ({
            id: stage.id,
            name: stage.name,
            // Use index + 1 as a proxy for level
            level: index + 1,
          }))
        );
      }
    } else {
      setCourseStages([]);
    }

    setSelectedFromStage("");
    setSelectedToStage("");
    setPageNumber(1);
  };

  const handleFromStageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedStageId = e.target.value;
    setSelectedFromStage(selectedStageId);

    // Auto-select the next stage if possible
    if (selectedStageId && courseStages.length > 0) {
      const currentStage = courseStages.find(
        (stage) => stage.id.toString() === selectedStageId
      );

      if (currentStage) {
        // Find the next stage based on level
        const nextStage = courseStages.find(
          (stage) => stage.level === currentStage.level + 1
        );

        if (nextStage) {
          setSelectedToStage(nextStage.id.toString());
        } else {
          // If there's no next stage, reset the to stage selection
          setSelectedToStage("");
        }
      }
    } else {
      // If no from stage is selected, reset to stage
      setSelectedToStage("");
    }

    setPageNumber(1);
  };

  const handleToStageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedToStage(e.target.value);
    setPageNumber(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageNumber(1);
  };

  // Handle pagination
  const handlePageChange = (page: number, pageSize?: number) => {
    setPageNumber(page);
    if (pageSize) setRecordsPerPage(pageSize);
  };

  // Get stage options based on selected course
  const getStageOptions = () => {
    return courseStages || [];
  };

  // Get "To Stage" options (only stages after the selected "From Stage")
  const getToStageOptions = () => {
    if (!selectedFromStage || !courseStages.length) return [];

    const currentStage = courseStages.find(
      (stage) => stage.id.toString() === selectedFromStage
    );

    if (!currentStage) return [];

    return courseStages
      .filter((stage) => stage.level > currentStage.level)
      .map((stage) => ({
        id: stage.id,
        name: stage.name,
      }));
  };

  // Check if all filters are selected
  const areAllFiltersSelected = () =>
    selectedSession && selectedCourse && selectedFromStage;

  // Check if all filters including 'to stage' are selected
  const areAllFiltersWithToStageSelected = () =>
    areAllFiltersSelected() && selectedToStage;

  // Get missing filters message
  const getMissingFiltersMessage = () => {
    const missingFilters = [];
    if (!selectedSession) missingFilters.push("Session");
    if (!selectedCourse) missingFilters.push("Course");
    if (!selectedFromStage) missingFilters.push("From Stage");

    let message = missingFilters.join(", ");
    if (selectedFromStage && !selectedToStage) {
      message += message ? ", To Stage" : "To Stage";
    }

    return message;
  };

  // Check if the selected stage is the last stage in the course
  const isLastStage = () => {
    if (!selectedFromStage || courseStages.length === 0) return false;

    // Find the current stage
    const currentStage = courseStages.find(
      (stage) => stage.id.toString() === selectedFromStage
    );

    if (!currentStage) return false;

    // Check if there are no stages with higher levels
    const higherStages = courseStages.filter(
      (stage) => stage.level > currentStage.level
    );

    return higherStages.length === 0;
  };

  return {
    // Filter state
    selectedSession,
    selectedCourse,
    selectedFromStage,
    selectedToStage,
    recordsPerPage,
    pageNumber,
    search,
    courseStages,

    // State setters
    setSelectedSession,
    setSelectedCourse,
    setSelectedFromStage,
    setSelectedToStage,
    setRecordsPerPage,
    setPageNumber,
    setSearch,
    setCourseStages,

    // Handlers
    handleSessionChange,
    handleCourseChange,
    handleFromStageChange,
    handleToStageChange,
    handleSearchChange,
    handlePageChange,

    // Helper functions
    getStageOptions,
    getToStageOptions,
    areAllFiltersSelected,
    areAllFiltersWithToStageSelected,
    getMissingFiltersMessage,
    isLastStage,
  };
};
